/**
 * CR306+CR311: Test runner panel — run test, display step results, offer-safe warnings.
 * CR311: Added postal code input for token context.
 */

import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Play,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ChevronDown,
  Clock,
  FileText,
  Link2,
  ShieldAlert,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import type { Recipe } from "@/lib/recipes-studio/types";
import type { RecipeTestRun, RecipeTestStep } from "@/lib/recipes-studio/config-types";
import { useRecipeTestRunner } from "@/hooks/useRecipeTestRunner";

interface RecipeTestRunnerProps {
  recipe: Recipe;
}

export function RecipeTestRunner({ recipe }: RecipeTestRunnerProps) {
  const { testing, testResult, testHistory, runTest, loadHistory } =
    useRecipeTestRunner();

  const [urlOverride, setUrlOverride] = useState("");
  const [postalCode, setPostalCode] = useState("M6K 3R8");

  useEffect(() => {
    setUrlOverride(recipe.default_start_url ?? "");
    loadHistory(recipe.id);
  }, [recipe.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRun = () => {
    const url = urlOverride.trim() || undefined;
    const context: Record<string, string> = {};
    if (postalCode.trim()) {
      context.postal_code = postalCode.trim();
    }
    runTest(recipe.id, url, Object.keys(context).length > 0 ? context : undefined);
  };

  const displayResult = testResult || testHistory[0] || null;

  return (
    <div className="space-y-4">
      {/* Test Controls */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Test Recipe</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">URL to test</Label>
            <Input
              type="url"
              value={urlOverride}
              onChange={(e) => setUrlOverride(e.target.value)}
              placeholder="https://example.com/deals"
              disabled={testing}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">
              Postal code{" "}
              <span className="text-muted-foreground font-normal">
                (used as <code className="bg-muted px-1 rounded text-[10px]">{"{{postal_code}}"}</code>)
              </span>
            </Label>
            <Input
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
              placeholder="M6K 3R8"
              disabled={testing}
              className="font-mono"
            />
          </div>
          <Button
            onClick={handleRun}
            disabled={testing || !urlOverride.trim()}
            className="w-full"
            size="sm"
          >
            {testing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Running…
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Test Recipe
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Test Results */}
      {displayResult && (
        <TestResultCard result={displayResult} isLatest={displayResult === testResult} />
      )}

      {/* History */}
      {testHistory.length > 1 && (
        <Collapsible>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="w-full justify-between text-xs">
              Previous runs ({testHistory.length - 1})
              <ChevronDown className="h-3 w-3" />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-2 mt-2">
            {testHistory.slice(1).map((run) => (
              <HistoryRow key={run.id} run={run} />
            ))}
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}

// ─── Sub-components ───

function TestResultCard({ result, isLatest }: { result: RecipeTestRun; isLatest: boolean }) {
  const [showRaw, setShowRaw] = useState(false);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">
            {isLatest ? "Latest Result" : "Execution Diagnostics"}
          </CardTitle>
          <StatusBadge status={result.status} />
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Timing */}
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {result.duration_ms ? `${(result.duration_ms / 1000).toFixed(1)}s` : "—"}
          </span>
          {result.links_found != null && (
            <span className="flex items-center gap-1">
              <Link2 className="h-3 w-3" />
              {result.links_found} links
            </span>
          )}
          {result.started_at && (
            <span>
              {formatDistanceToNow(new Date(result.started_at), { addSuffix: true })}
            </span>
          )}
        </div>

        {/* Offer-safe violations */}
        {result.offer_safe_violations && result.offer_safe_violations.length > 0 && (
          <div className="border border-destructive/30 rounded-md p-2 bg-destructive/5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-destructive mb-1">
              <ShieldAlert className="h-3.5 w-3.5" />
              Offer-safe violations ({result.offer_safe_violations.length})
            </div>
            <ul className="text-xs text-destructive/80 space-y-0.5 ml-5 list-disc">
              {result.offer_safe_violations.map((v, i) => (
                <li key={i} className="font-mono text-[11px]">
                  {v}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Steps */}
        {result.steps && result.steps.length > 0 && (
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Steps</Label>
            {result.steps.map((step) => (
              <StepRow key={step.id || step.step_index} step={step} />
            ))}
          </div>
        )}

        {/* Markdown preview */}
        {result.markdown_preview && (
          <Collapsible>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="w-full justify-between text-xs h-7">
                <span className="flex items-center gap-1">
                  <FileText className="h-3 w-3" />
                  Markdown Preview
                </span>
                <ChevronDown className="h-3 w-3" />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <ScrollArea className="h-[200px] mt-1">
                <pre className="text-[11px] font-mono bg-muted/50 p-2 rounded whitespace-pre-wrap">
                  {result.markdown_preview}
                </pre>
              </ScrollArea>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Error */}
        {result.error_message && (
          <div className="border border-destructive/30 rounded-md p-2 bg-destructive/5">
            <p className="text-xs text-destructive font-medium">
              {result.error_code && <span className="font-mono">[{result.error_code}] </span>}
              {result.error_message}
            </p>
          </div>
        )}

        {/* Raw response toggle */}
        {result.config_snapshot && (
          <Collapsible open={showRaw} onOpenChange={setShowRaw}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="text-xs h-7">
                {showRaw ? "Hide" : "Show"} config snapshot
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <ScrollArea className="h-[150px] mt-1">
                <pre className="text-[10px] font-mono bg-muted/50 p-2 rounded whitespace-pre-wrap">
                  {JSON.stringify(result.config_snapshot, null, 2)}
                </pre>
              </ScrollArea>
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  );
}

function StepRow({ step }: { step: RecipeTestStep }) {
  return (
    <div className="flex items-start gap-2 text-xs py-1 px-2 rounded bg-muted/30">
      {step.success ? (
        <CheckCircle2 className="h-3.5 w-3.5 text-primary mt-0.5 flex-shrink-0" />
      ) : (
        <XCircle className="h-3.5 w-3.5 text-destructive mt-0.5 flex-shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <span className="font-medium">{step.step_type}</span>
        {step.chosen_label && (
          <span className="text-muted-foreground ml-1">— {step.chosen_label}</span>
        )}
        {step.duration_ms != null && (
          <span className="text-muted-foreground ml-1">({step.duration_ms}ms)</span>
        )}
        {step.evidence_snippet && (
          <p className="text-muted-foreground truncate mt-0.5 font-mono text-[10px]">
            {step.evidence_snippet}
          </p>
        )}
        {step.error_message && (
          <p className="text-destructive mt-0.5">{step.error_message}</p>
        )}
      </div>
      {step.match_count != null && (
        <Badge variant="outline" className="text-[9px] px-1 h-4 flex-shrink-0">
          {step.match_count}
        </Badge>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case "COMPLETE":
      return (
        <Badge variant="outline" className="text-[10px] border-primary/30 text-primary">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Complete
        </Badge>
      );
    case "FAILED":
      return (
        <Badge variant="destructive" className="text-[10px]">
          <XCircle className="h-3 w-3 mr-1" />
          Failed
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="text-[10px]">
          <AlertTriangle className="h-3 w-3 mr-1" />
          {status}
        </Badge>
      );
  }
}

function HistoryRow({ run }: { run: RecipeTestRun }) {
  return (
    <div className="flex items-center justify-between p-2 rounded border text-xs">
      <div className="flex items-center gap-2">
        <StatusBadge status={run.status} />
        <span className="text-muted-foreground truncate max-w-[180px]">{run.start_url}</span>
      </div>
      <span className="text-muted-foreground text-[10px]">
        {run.duration_ms ? `${(run.duration_ms / 1000).toFixed(1)}s` : "—"}
        {" · "}
        {formatDistanceToNow(new Date(run.created_at), { addSuffix: true })}
      </span>
    </div>
  );
}
